import {
  Grid,
  GridColumn as Column,
  GridColumnMenuFilter,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import * as React from "react";
import { Input } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { useNavigate } from "react-router-dom";
import ClientContext from "../../layouts/ClientContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useQueryGetClientsStatusReportPage,
  fetchAllClientsStatusReportRows,
} from "../../data/services/clientService";
import { useQueryGetCarrierList } from "../../data/services/carrierService";
import { useQueryGetBrokerUsersList } from "../../data/services/brokerUserService";
import { useQueryGetPrismInternalUsers } from "../../data/services/salesAdminMappingService";
import { useQueryGetNotificationWaves } from "../../data/services/notificationService";
import { saveClientDashboardWorkbook } from "./clientDashboardExport";
import UserContext from "../../layouts/UserContext";
import OEContext from "../../layouts/OEContext";
import {
  faChevronDown,
  faChevronUp,
  faClockRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import AuditTrailModal from "../client/AuditTrailModal";
import { createPortal } from "react-dom";
import useDebounce from "../../hooks/useDebounce";
import {
  getRenewalBadge,
  RENEWAL_FILTER_OPTIONS,
} from "../../constants/renewalEligibility";
import MedicalOnlyCostNote from "../../components/MedicalOnlyCostNote";
import {
  StatusColumnMenu,
  createCheckboxColumnMenu,
  getFilterValues,
  toCheckboxOptions,
} from "./parentDashboardStatusFilter";
import "@progress/kendo-theme-default/dist/all.css";

const RenewalColumnMenu = createCheckboxColumnMenu(RENEWAL_FILTER_OPTIONS);

// Matches the Parent Renewal Dashboard's Export to Excel button: Microsoft
// Excel's own icon green while clickable, so an available action reads
// differently from an unavailable one.
const EXCEL_GREEN = "#217346";
const EXCEL_BUTTON_ACTIVE_STYLE = {
  backgroundColor: EXCEL_GREEN,
  borderColor: EXCEL_GREEN,
  color: "#fff",
};

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { setClient } = React.useContext(ClientContext);
  const { loggedInUser } = React.useContext(UserContext);
  const oePeriod = React.useContext(OEContext);
  const gridRef = React.useRef(null);
  const isAdminUser = !!loggedInUser?.hasAdminAppPrivilege;
  const isBenefitAdminUser = !!loggedInUser?.benefitAdminPrivilege;

  const [gridExpand, setGridExpand] = React.useState({});
  const [showAllClients, setShowAllClients] = React.useState("Yes");
  const [allClientOption, setAllClientOption] = React.useState("All Clients");
  const [searchTerm, setSearchTerm] = React.useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [gridDataState, setGridDataState] = React.useState({
    take: 20,
    skip: 0,
    sort: [],
    filter: { logic: "and", filters: [] },
  });

  const [showAuditTrailModelDialog, setShowAuditTrailModelDialog] =
    React.useState(false);
  const [selectedClient, setSelectedClient] = React.useState({});

  const page = Math.floor(gridDataState.skip / gridDataState.take) + 1;

  // Checkbox filters (Status, Renewal) can carry several ticked values; text
  // filters (Broker, Benefit Rep, Sales rep, Carrier) carry at most one. Either
  // way getFilterValues just extracts whatever's set for that field.
  const statusFilter = getFilterValues(gridDataState.filter, "oeStatusDesc").join(",");
  const renewalFilter = getFilterValues(gridDataState.filter, "renewalHandlingMode").join(
    ",",
  );
  const brokerFilter = getFilterValues(gridDataState.filter, "broker").join(",");
  const benefitRepFilter = getFilterValues(gridDataState.filter, "benefitRepName").join(
    ",",
  );
  const salesRepFilter = getFilterValues(gridDataState.filter, "salesRepName").join(",");
  const carrierFilter = getFilterValues(gridDataState.filter, "carrierName").join(",");
  const waveFilter = getFilterValues(gridDataState.filter, "wave").join(",");

  const hasActiveFilters = !!gridDataState.filter?.filters?.length;

  // Broker, Benefit Rep, Sales rep and Carrier are all bounded, admin-managed
  // lists, so each checkbox menu's options come from the full list rather than a
  // free-text search -- reusing the same lists the broker/carrier/sales-admin
  // setup screens already use.
  const { data: carrierListResponse } = useQueryGetCarrierList();
  const carrierOptions = React.useMemo(
    () =>
      toCheckboxOptions(
        (carrierListResponse?.data || []).map((carrier) => carrier.carrierName),
      ),
    [carrierListResponse],
  );
  const CarrierColumnMenu = React.useMemo(
    () => createCheckboxColumnMenu(carrierOptions),
    [carrierOptions],
  );

  // Broker and the two reps can run to dozens of names, unlike the short fixed
  // lists above -- so these get the searchable variant of the same menu.
  const { data: brokerListResponse } = useQueryGetBrokerUsersList();
  const brokerOptions = React.useMemo(
    () =>
      toCheckboxOptions(
        (brokerListResponse?.data || []).map((broker) =>
          [broker.firstName, broker.lastName].filter(Boolean).join(" "),
        ),
      ),
    [brokerListResponse],
  );
  const BrokerColumnMenu = React.useMemo(
    () => createCheckboxColumnMenu(brokerOptions, { searchable: true }),
    [brokerOptions],
  );

  // Benefit Rep and Sales rep both draw from the same PrismHR internal-user pool,
  // so they share one options list and one menu component.
  const { data: prismInternalUsersResponse } = useQueryGetPrismInternalUsers();
  const repOptions = React.useMemo(
    () =>
      toCheckboxOptions(
        (prismInternalUsersResponse?.data || []).map((user) => user.userName),
      ),
    [prismInternalUsersResponse],
  );
  const RepColumnMenu = React.useMemo(
    () => createCheckboxColumnMenu(repOptions, { searchable: true }),
    [repOptions],
  );

  // Notification Wave names are admin-defined per OE period (not a fixed
  // enum), so its options come from the same period-scoped lookup the
  // Notification Wave setup screens use, rather than a hard-coded list.
  const { data: notificationWavesResponse } = useQueryGetNotificationWaves(
    oePeriod?.oePeriodId,
  );
  const waveOptions = React.useMemo(
    () =>
      toCheckboxOptions(
        (notificationWavesResponse?.data || []).map((wave) => wave.name),
      ),
    [notificationWavesResponse],
  );
  const WaveColumnMenu = React.useMemo(
    () => createCheckboxColumnMenu(waveOptions),
    [waveOptions],
  );

  const [isExporting, setIsExporting] = React.useState(false);
  const [exportError, setExportError] = React.useState(null);

  const { data: apiResponse, isFetching } = useQueryGetClientsStatusReportPage(
    oePeriod?.oePeriodId,
    showAllClients,
    page,
    gridDataState.take,
    gridDataState.sort,
    debouncedSearchTerm,
    statusFilter,
    renewalFilter,
    brokerFilter,
    benefitRepFilter,
    salesRepFilter,
    carrierFilter,
    waveFilter,
  );

  const pagedData = apiResponse?.data;
  const mainRows = pagedData?.items || [];

  React.useEffect(() => {
    if (loggedInUser && !loggedInUser.isWhiteListed)
      navigate("/under-maintenance");
  }, [loggedInUser, navigate]);

  // The grid is server-paged, so exporting what is on screen would silently
  // produce 20 rows out of a thousand. This pulls every page first, forwarding
  // every active filter and the current sort so the export matches what the
  // user is actually looking at.
  const onExportToExcel = async () => {
    if (isExporting) return;
    setIsExporting(true);
    setExportError(null);
    try {
      const rows = await fetchAllClientsStatusReportRows(
        oePeriod?.oePeriodId,
        showAllClients,
        debouncedSearchTerm,
        gridDataState.sort,
        statusFilter,
        renewalFilter,
        brokerFilter,
        benefitRepFilter,
        salesRepFilter,
        carrierFilter,
        waveFilter,
      );

      const safePeriod = (oePeriod?.oePeriodName || "export").replace(
        /[^A-Za-z0-9._-]+/g,
        "-",
      );

      await saveClientDashboardWorkbook(rows, `assigned-clients-${safePeriod}.xlsx`);
    } catch {
      setExportError(
        "The export could not be completed. Please try again; if it keeps failing, contact support.",
      );
    } finally {
      setIsExporting(false);
    }
  };

  const onClientFilterChange = (e) => {
    setShowAllClients(e.value === "All Clients" ? "Yes" : "No");
    setAllClientOption(e.value);
    setGridDataState({ ...gridDataState, skip: 0 });
  };

  const parentClientIdCell = (props) => {
    const { parentClientId, clientId } = props.dataItem;
    const displayValue =
      parentClientId == null || parentClientId === clientId
        ? "-"
        : parentClientId;
    return <td>{displayValue}</td>;
  };

  const actionCell = (props) => {
    if (
      !props.dataItem.oePeriodId &&
      props.dataItem.clientId === props.dataItem.parentClientId
    ) {
      return (
        <td>
          <span style={{ color: "grey", fontSize: "11px" }}>Parent Group</span>
        </td>
      );
    }
    if (props.rowType === "groupHeader") {
      return null;
    }

    const isParentCompany =
      props.dataItem.clientId === props.dataItem.parentClientId;

    return (
      <td>
        <div style={{ display: "flex", alignItems: "center" }}>
          {isAdminUser ? (
            <>
              <Button
                onClick={() => {
                  setClient(props.dataItem);
                  navigate(`/admin/clients/${props.dataItem.clientId}/setup`);
                }}
              >
                View Setup
              </Button>
              <Button
                themeColor="info"
                style={{ marginLeft: "10px" }}
                onClick={() => {
                  setClient({ ...props.dataItem, parent: isParentCompany });
                  navigate(
                    `/admin/clients/${props.dataItem.clientId}/oe/${props.dataItem.oePeriodId}/summary`,
                  );
                }}
              >
                View Rates
              </Button>
            </>
          ) : (
            <Button
              themeColor="info"
              onClick={() => {
                setClient(props.dataItem);
                navigate(
                  `/clients/${props.dataItem.clientId}/oe/${props.dataItem.oePeriodId}/summary`,
                );
              }}
            >
              Review
            </Button>
          )}
          <FontAwesomeIcon
            icon={faClockRotateLeft}
            onClick={() => {
              setSelectedClient(props.dataItem);
              setShowAuditTrailModelDialog(true);
            }}
            style={{
              color: "grey",
              fontSize: "20px",
              cursor: "pointer",
              marginLeft: "10px",
            }}
          />
        </div>
      </td>
    );
  };

  const statusCell = (props) => {
    if (props.rowType === "groupHeader") {
      return null;
    }
    if (!props.dataItem.oeStatusDesc) {
      return <td />;
    }
    const s = props.dataItem.oeStatus;
    const d = props.dataItem.oeStatusDesc;
    const colorMap = {
      InProgress: "orange",
      Submitted: "green",
      Published: "green",
      PublishError: "red",
      PublishInProgress: "yellow",
    };
    const bg = colorMap[s] || "grey";
    return (
      <td>
        <span
          style={{
            padding: "2px 8px",
            backgroundColor: bg,
            borderRadius: "15px",
            color: bg === "yellow" ? "black" : "white",
            fontSize: "12px",
          }}
        >
          {s === "NotStarted"
            ? d
            : s === "Submitted" && !isAdminUser
              ? "Submitted"
              : d}
        </span>
      </td>
    );
  };

  const renewalCell = (props) => {
    const badge = getRenewalBadge(props.dataItem);
    if (!badge) {
      return <td />;
    }

    const backgroundColor =
      badge.themeColor === "warning" ? "#b07d00" : "#0d6efd";

    return (
      <td>
        <span
          title={badge.title}
          style={{
            paddingLeft: "8px",
            paddingRight: "8px",
            paddingTop: "1px",
            paddingBottom: "1px",
            backgroundColor: backgroundColor,
            borderRadius: "15px",
            color: "white",
            fontSize: "12px",
          }}
        >
          {badge.text}
        </span>
      </td>
    );
  };

  const loadingPanelMarkup = (
    <div className="k-loading-mask">
      <span className="k-loading-text">Loading</span>
      <div className="k-loading-image" />
      <div className="k-loading-color" />
    </div>
  );

  const LoadingPanel = (props) => {
    const { gridRef } = props;
    const gridContent =
      gridRef.current && gridRef.current.querySelector(".k-grid-content");
    return gridContent
      ? createPortal(loadingPanelMarkup, gridContent)
      : loadingPanelMarkup;
  };

  const DetailComponent = (props) => {
    const subClients = props.dataItem.subClients;

    if (!subClients || subClients.length === 0) {
      return null;
    }

    return (
      <Grid data={subClients} scrollable="none">
        <Column field="clientId" title="Company ID" width="120px" />
        <Column field="clientName" title="Company Name" />
        <Column
          field="parentClientId"
          title="Parent ID"
          width="120px"
          cell={parentClientIdCell}
        />
        <Column field="broker" title="Broker" />
        {isAdminUser && <Column field="benefitRepName" title="Benefit Rep" />}
        {isAdminUser && <Column field="salesRepName" title="Sales rep" />}
        <Column field="currentRate" title="Current Cost" format="{0:c}" />
        <Column
          field="percentageIncrease"
          title="Percentage Increase"
          format="{0:p2}"
        />
        <Column field="wave" title="Notification Wave" />
        <Column field="notificationSent" title="Notification Sent" />
        <Column field="oeStatusDesc" title="Status" cell={statusCell} />
        <Column
          field="renewalHandlingMode"
          title="Renewal"
          cell={renewalCell}
          sortable={false}
        />
        <Column
          field="carrierName"
          width={"140"}
          title="Carrier"
          sortable={false}
          filterable={false}
        />
        <Column
          title="Action"
          cell={actionCell}
          width="240px"
          sortable={false}
        />
        <Column field="submissionRecord" title="Submission Record" />
      </Grid>
    );
  };

  const expandCell = (props) => {
    if (props.rowType === "groupHeader") {
      return null;
    }

    const hasSubClients = (props.dataItem.subClients || []).length > 0;

    return (
      <td>
        {hasSubClients ? (
          <Button
            themeColor="secondary"
            size="small"
            fillMode="flat"
            rounded="full"
            aria-label={
              gridExpand[props.dataItem.clientId]
                ? "Hide child companies"
                : "Show child companies"
            }
            style={{ minWidth: "32px", width: "32px", padding: "4px" }}
            onClick={() => {
              setGridExpand((currentState) => ({
                ...currentState,
                [props.dataItem.clientId]:
                  !currentState[props.dataItem.clientId],
              }));
            }}
          >
            <FontAwesomeIcon
              icon={
                gridExpand[props.dataItem.clientId]
                  ? faChevronUp
                  : faChevronDown
              }
            />
          </Button>
        ) : null}
      </td>
    );
  };

  const customCellRender = (td, props) => {
    if (
      props?.rowType === "data" &&
      td?.props?.className?.includes("k-hierarchy-cell")
    ) {
      return <td className="k-hierarchy-cell" />;
    }
    return td;
  };

  // A filter change has to land the user back on page one: the grid is
  // server-paged, so keeping skip from the old, larger result set asks the
  // server for a page the filtered set no longer has and renders an empty grid.
  const withFilterPageReset = (nextDataState) => {
    // Kendo signals "nothing filtered" as null from a menu's Clear and as an
    // empty filters array from the Clear Filters button, so normalize before
    // comparing -- otherwise switching between the two reads as a change.
    const normalize = (filter) =>
      filter?.filters?.length ? JSON.stringify(filter) : "";
    const filterChanged =
      normalize(nextDataState.filter) !== normalize(gridDataState.filter);
    return filterChanged ? { ...nextDataState, skip: 0 } : nextDataState;
  };

  const columnMenuHeader = (field) =>
    GridColumnMenuFilter.active(field, gridDataState.filter) ? "active" : "";

  return (
    <div>
      {!isAdminUser && (
        <div
          style={{
            backgroundColor: "#cce5ff",
            padding: "10px",
            marginTop: 20,
            borderRadius: ".25rem",
            textAlign: "center",
          }}
        >
          <span style={{ color: "#004085" }}>
            Welcome to {oePeriod?.oePeriodName ?? "Open Enrollment"}. Please
            select your company below to review your current setup and create
            comparison models with new contributions.
          </span>
        </div>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          margin: "20px 0",
        }}
      >
        <div style={{ display: "flex", width: "50%" }}>
          <Input
            placeholder="Search Company Id or Name..."
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setGridDataState({ ...gridDataState, skip: 0 });
            }}
            value={searchTerm}
          />
          {isBenefitAdminUser && (
            <DropDownList
              style={{ width: "150px", marginLeft: "10px" }}
              data={["All Clients", "My Clients"]}
              value={allClientOption}
              onChange={onClientFilterChange}
            />
          )}
          <Button
            themeColor="base"
            style={{
              marginLeft: "10px",
              whiteSpace: "nowrap",
              ...(isExporting ? {} : EXCEL_BUTTON_ACTIVE_STYLE),
            }}
            onClick={onExportToExcel}
            disabled={isExporting}
          >
            {isExporting ? "Exporting..." : "Export to Excel"}
          </Button>
        </div>
        <Button
          // Matches the checkbox menu's own "Filter" button, so Clear Filters
          // reads as the undo of that action rather than an unrelated color.
          themeColor={hasActiveFilters ? "primary" : "base"}
          style={{ whiteSpace: "nowrap" }}
          disabled={!hasActiveFilters}
          onClick={() =>
            setGridDataState({
              ...gridDataState,
              skip: 0,
              filter: { logic: "and", filters: [] },
            })
          }
        >
          Clear Filters
        </Button>
      </div>
      {exportError && (
        <div
          role="alert"
          style={{
            padding: "10px 14px",
            marginBottom: "12px",
            border: "1px solid #c53030",
            backgroundColor: "#fff5f5",
            borderRadius: "4px",
            color: "#c53030",
          }}
        >
          {exportError}
        </div>
      )}
      {isFetching ? <LoadingPanel gridRef={gridRef} /> : null}
      <Grid
        style={{ height: "700px" }}
        total={pagedData?.totalCount || 0}
        {...gridDataState}
        loading={isFetching}
        onDataStateChange={(e) => setGridDataState(withFilterPageReset(e.dataState))}
        pageable={{ buttonCount: 5, pageSizes: [10, 20, 50] }}
        sortable={true}
        cellRender={customCellRender}
        data={mainRows.map((item) => ({
          ...item,
          expanded: !!gridExpand[item.clientId],
        }))}
        expandField="expanded"
        onExpandChange={(e) => {
          setGridExpand({
            ...gridExpand,
            [e.dataItem.clientId]: e.value,
          });
        }}
        detail={DetailComponent}
      >
        <Column field="clientId" title="Company ID" width="120px" />
        <Column field="clientName" title="Company Name" />
        <Column
          field="parentClientId"
          title="Parent ID"
          width="120px"
          cell={parentClientIdCell}
        />
        <Column
          field="broker"
          title="Broker"
          filter="text"
          columnMenu={BrokerColumnMenu}
          headerClassName={columnMenuHeader("broker")}
        />
        {isAdminUser && (
          <Column
            field="benefitRepName"
            title="Benefit Rep"
            filter="text"
            columnMenu={RepColumnMenu}
            headerClassName={columnMenuHeader("benefitRepName")}
          />
        )}
        {isAdminUser && (
          <Column
            field="salesRepName"
            title="Sales rep"
            filter="text"
            columnMenu={RepColumnMenu}
            headerClassName={columnMenuHeader("salesRepName")}
          />
        )}
        <Column field="currentRate" title="Current Cost" format="{0:c}" />
        <Column
          field="percentageIncrease"
          title="Percentage Increase"
          format="{0:p2}"
        />
        <Column
          field="wave"
          title="Notification Wave"
          filter="text"
          columnMenu={WaveColumnMenu}
          headerClassName={columnMenuHeader("wave")}
        />
        <Column field="notificationSent" title="Notification Sent" />
        <Column
          field="oeStatusDesc"
          title="Status"
          cell={statusCell}
          filter="text"
          columnMenu={StatusColumnMenu}
          headerClassName={columnMenuHeader("oeStatusDesc")}
        />
        <Column
          field="renewalHandlingMode"
          title="Renewal"
          cell={renewalCell}
          filter="text"
          columnMenu={RenewalColumnMenu}
          headerClassName={columnMenuHeader("renewalHandlingMode")}
        />
        <Column
          field="carrierName"
          width={"140"}
          title="Carrier"
          filter="text"
          columnMenu={CarrierColumnMenu}
          headerClassName={columnMenuHeader("carrierName")}
        />
        <Column
          title="Action"
          cell={actionCell}
          width="240px"
          sortable={false}
        />
        <Column field="submissionRecord" title="Submission Record" />
        <Column title="" cell={expandCell} sortable={false} width="60px" />
      </Grid>
      <MedicalOnlyCostNote />
      {showAuditTrailModelDialog && (
        <AuditTrailModal
          client={selectedClient}
          onModelClose={() => setShowAuditTrailModelDialog(false)}
        />
      )}
    </div>
  );
};

export default ClientDashboard;
